import React, { CSSProperties, useEffect, useState } from 'react';
import InView from 'react-intersection-observer';

import { GalleryItem } from './components/GalleryItem/GalleryItem';
import { LoadMoreButton } from './components/LoadMoreButton';
// import { OpenseaAsset } from './types/OpenseaAsset';
import { isEnsDomain, joinClassNames } from './utils';
import {
  fetchOpenseaAssets,
  OPENSEA_API_OFFSET,
  resolveEnsDomain,
} from './api';

import '../dist/output.css';
import { SkeletonCard } from './components/SkeletonCard';
import { useLightboxNavigation } from './hooks/useLightboxNavigation';

export const NftGallery = ({
  ownerAddress = '',
  openseaApiKey = '',
  darkMode = false,
  metadataIsVisible = true,
  showcaseMode = false,
  showcaseItemIds,
  hasLightbox = true,
  hasExternalLinks = true,
  isInline = false,
  hasLoadMoreButton = false,
  galleryContainerStyle,
  itemContainerStyle,
  imgContainerStyle,
}) => {
  const [assets, setAssets] = useState([] );
  const [showcaseAssets, setShowcaseAssets] = useState([] );
  const [currentOffset, setCurrentOffset] = useState(0);
  const [canLoadMore, setCanLoadMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    lightboxIndex,
    setLightboxIndex,
    decreaseLightboxIndex,
    increaseLightboxIndex,
  } = useLightboxNavigation(assets.length - 1);

  const displayedAssets = showcaseMode ? showcaseAssets : assets;

  const loadAssets = async (
    ownerAddress,
    apiKey,
    offset
  ) => {
    setIsLoading(true);
    const owner = isEnsDomain(ownerAddress)
      ? await resolveEnsDomain(ownerAddress)
      : ownerAddress;
    const rawAssets = await fetchOpenseaAssets({
      owner,
      apiKey,
      offset,
    });
    console.log(rawAssets)
    setAssets((prevAssets) => [...prevAssets, ...rawAssets]);
    setCanLoadMore(rawAssets.length === OPENSEA_API_OFFSET);
    setIsLoading(false);
  };

  const loadShowcaseAssets = async (
    ownerAddress,
    apiKey
  ) => {
    setIsLoading(true);
    // Stop if we already have 1000+ items in play.
    const MAX_OFFSET = OPENSEA_API_OFFSET * 20;
    const owner = isEnsDomain(ownerAddress)
      ? await resolveEnsDomain(ownerAddress)
      : ownerAddress;

    let shouldFetch = true;
    let currentOffset = 0;

    // Grab all assets of this address to filter down to showcase-only.
    // TODO: optimise this to exit as soon as all showcase items have been resolved.
    while (shouldFetch) {
      const rawAssets = await fetchOpenseaAssets({
        owner,
        apiKey,
        offset: currentOffset,
      });
      setAssets((prevAssets) => [...prevAssets, ...rawAssets]);
      currentOffset += OPENSEA_API_OFFSET;
      if (rawAssets.length !== 0) setIsLoading(false);

      // Terminate if hit the global limit or we hit a non-full page (i.e. end of assets).
      if (
        rawAssets.length < OPENSEA_API_OFFSET ||
        currentOffset >= MAX_OFFSET
      ) {
        shouldFetch = false;
        setIsLoading(false);
      }
    }
  };

  const updateShowcaseAssets = (
    allAssets,
    itemIds
  ) => {
    const nextShowcaseAssets = allAssets.filter((asset) =>
      itemIds.includes(`${asset.asset_contract.address}/${asset.token_id}`)
    );
    setShowcaseAssets(nextShowcaseAssets);
  };

  const onLastItemInView = (isInView) => {
    if (!hasLoadMoreButton && isInView) {
      setCurrentOffset((prevOffset) => prevOffset + OPENSEA_API_OFFSET);
    }
  };

  // TODO: Move into `Lightbox` component once its refactored to being a singleton.
  const handleKeydownEvent = (evt) => {
    const hasActiveLightbox =
      window.location.hash.includes('lightbox-') &&
      window.location.hash !== '#lightbox-untarget';

    if (!hasActiveLightbox) {
      return;
    }

    switch (evt.key) {
      case 'ArrowLeft':
        return decreaseLightboxIndex();
      case 'ArrowRight':
        return increaseLightboxIndex();
      case 'Escape':
        return window.location.assign(`#lightbox-untarget`);
      default:
        break;
    }
  };

  // Handles fetching of assets via OpenSea API.
  useEffect(() => {
    if (showcaseMode) {
      loadShowcaseAssets(ownerAddress, openseaApiKey);
    } else {
      loadAssets(ownerAddress, openseaApiKey, currentOffset);
    }
  }, [showcaseMode, ownerAddress, openseaApiKey, currentOffset]);

  // Isolates assets specified for showcase mode into their own collection whenever `assets` changes.
  useEffect(() => {
    if (assets.length !== 0 && showcaseMode && Array.isArray(showcaseItemIds)) {
      updateShowcaseAssets(assets, showcaseItemIds);
    }
  }, [assets, showcaseMode, showcaseItemIds]);

  // Binds/unbinds keyboard event listeners.
  useEffect(() => {
    document.addEventListener('keydown', handleKeydownEvent);
    return () => {
      document.removeEventListener('keydown', handleKeydownEvent);
    };
  }, [assets, handleKeydownEvent, lightboxIndex]);

  const renderSkeletonCards = () =>
    new Array(8)
      .fill(0)
      .map((_, index) => <SkeletonCard key={'placeholder-' + index} />);

  return (
    <div
      className={joinClassNames(darkMode ? 'rnftg-dark' : '', 'rnftg-h-full')}
    >
      <div
        style={galleryContainerStyle}
        className={joinClassNames(
          'rnftg-h-full rnftg-p-6 rnftg-overflow-auto rnftg-bg-gray-50 dark:rnftg-bg-gray-900',
          isInline ? 'rnftg--inline' : ''
        )}
      >
        <div
          className={joinClassNames(
            'rnftg-flex',
            isInline ? 'rnftg-flex-row' : 'rnftg-flex-col'
          )}
        >
          <div
            className={joinClassNames(
              'rnftg-grid rnftg-gap-6',
              isInline
                ? 'rnftg-grid-flow-col'
                : 'rnftg-grid-flow-row rnftg-grid-cols-1 md:rnftg-grid-cols-2 lg:rnftg-grid-cols-3 xl:rnftg-grid-cols-4'
            )}
          >
            {displayedAssets.length === 0 && isLoading
              ? renderSkeletonCards()
              : displayedAssets.map((asset, index) => {
                  const item = (
                    <GalleryItem
                      key={asset.id}
                      index={index}
                      asset={asset}
                      metadataIsVisible={metadataIsVisible}
                      hasLightbox={hasLightbox}
                      setLightboxIndex={setLightboxIndex}
                      increaseLightboxIndex={increaseLightboxIndex}
                      decreaseLightboxIndex={decreaseLightboxIndex}
                      hasExternalLinks={hasExternalLinks}
                      itemContainerStyle={itemContainerStyle}
                      imgContainerStyle={imgContainerStyle}
                    />
                  );
                  const isLastItemInPage =
                    (index + 1) % OPENSEA_API_OFFSET === 0;
                  return isLastItemInPage ? (
                    item
                  ) : (
                    item
                  );
                })}
          </div>
          {/* {hasLoadMoreButton && canLoadMore && (
            <LoadMoreButton
              onClick={() => {
                setCurrentOffset(
                  (prevOffset) => prevOffset + OPENSEA_API_OFFSET
                );
              }}
            />
          )} */}
        </div>
      </div>
    </div>
  );
};
